import { UseQueryResult } from '@tanstack/react-query'
import {
  CspLowerEnum,
  LakehouseConnectionTypeEnum,
  LakehouseMySqlConnection,
  LakehouseMySqlConnectionInput,
  LakehousePostgresConnection,
  LakehousePostgresConnectionInput,
  LakehouseTable,
  MySqlSslMode,
  NodeInput,
  PostgresSslMode,
  TableColumnInput,
} from '@vendia/management-api-types'
import { ControlPlaneClient, GetLakehouseTablesDocument } from '@vendia/control-plane-api'
import debug from 'debug'
import { AWS_REGIONS } from 'src/utils/csp/regions'
import notify from 'src/utils/notify'

const logger = debug('app:Util')

// Check to see if a Snowflake type is a string type, allow string transformations, etc
export function isSnowflakeStringType(type: string) {
  return (
    type.toUpperCase().startsWith('CHAR') ||
    type.toUpperCase().startsWith('VARCHAR') ||
    type.toUpperCase().startsWith('STRING')
  )
}

export function snowflakeToJsonSchema(columns: TableColumnInput[]) {
  const properties: Record<string, any> = {}
  const jsonSchema = {
    type: 'object',
    properties,
    required: [],
    'x-vendia-unique': [],
  }

  const typeMapping: { [key: string]: string } = {
    NUMBER: 'number',
    FLOAT: 'number',
    INT: 'integer',
    INTEGER: 'integer',
    BIGINT: 'integer',
    SMALLINT: 'integer',
    TINYINT: 'integer',
    BYTEINT: 'integer',
    VARCHAR: 'string',
    CHAR: 'string',
    CHARACTER: 'string',
    STRING: 'string',
    TEXT: 'string',
    BOOLEAN: 'boolean',
    DATE: 'string',
    TIME: 'string',
    TIMESTAMP: 'string',
    TIMESTAMP_LTZ: 'string',
    TIMESTAMP_NTZ: 'string',
    TIMESTAMP_TZ: 'string',
    VARIANT: 'object',
    OBJECT: 'object',
    ARRAY: 'array',
  }

  columns.forEach((column) => {
    const columnName = column.name.toLowerCase()
    const type = column.type
    const jsonType = typeMapping[type.split('(')[0].toUpperCase()] || 'string'

    jsonSchema.properties[columnName] = {
      type: jsonType,
      ['x-snowflake-type']: type,
    }

    // Add format for date and time types
    if (type.toUpperCase().startsWith('DATE')) {
      jsonSchema.properties[columnName].format = 'date'
    } else if (type.toUpperCase().startsWith('TIMESTAMP')) {
      jsonSchema.properties[columnName].format = 'date-time'
    } else if (type.toUpperCase().startsWith('TIME')) {
      jsonSchema.properties[columnName].format = 'time'
    }
  })
  return jsonSchema
}

// Trim, lowercase, replace whitespace with dashes, strip special characters, add random suffix
// Input: "My Uni Name 123!@#   "
// Output: "my-uni-name-123-4j5k"
export const buildUniNameFromAlias = (alias: string, randomSuffix: string) => {
  return alias
    .trim()
    .replace(/\s+/g, '-')
    .replace(/_/g, '-')
    .replace(/[^a-zA-Z0-9_-]/g, '')
    .toLowerCase()
    .substring(0, 20)
    .concat('-', randomSuffix)
}

export const buildUniFqn = (uniName: string, uniNamespace: string) => {
  if (!uniNamespace) {
    return `${uniName}.unis.vendia.net`
  }
  if (uniNamespace.split('.').filter((x) => x).length === 2) {
    return `${uniName}.unis${uniNamespace}`
  }
  return `${uniName}${uniNamespace}`
}

export function generateNodesForApi(
  userId: string,
  nodes: { name: string; description?: string; csp: CspLowerEnum; region: string }[],
) {
  // Lowercase here just in case we didn't lowercase in signup/invite flow
  userId = userId?.toLowerCase()

  return nodes.map(({ name, description, csp, region }) => {
    const nodeConfig: NodeInput = {
      name,
      description,
      userId,
      csp,
      region,
      settings: {
        apiSettings: {
          auth: {
            authorizerType: 'AUTHV2',
          },
        },
      },
    }

    switch (csp) {
      case 'aws':
        nodeConfig.settings.aws = {}
        break
      case 'azure':
        nodeConfig.settings.azure = {}
        break
      default:
        console.error('An unexpected error occurred, please try again')
        break
    }

    return nodeConfig
  })
}

/* All AWS Snowflake regions to AWS regions
  AWS_US_WEST_2: us-west-2
  AWS_US_EAST_1: us-east-1
  AWS_AP_SOUTHEAST_2: ap-southeast-2
  AWS_EU_WEST_1: eu-west-1
  AWS_AP_SOUTHEAST_1: ap-southeast-1
  AWS_CA_CENTRAL_1: ca-central-1
  AWS_EU_CENTRAL_1: eu-central-1
  AWS_US_EAST_2: us-east-2
  AWS_AP_NORTHEAST_1: ap-northeast-1
  AWS_AP_SOUTH_1: ap-south-1
  AWS_EU_WEST_2: eu-west-2
  AWS_AP_NORTHEAST_2: ap-northeast-2
  AWS_EU_NORTH_1: eu-north-1
  AWS_AP_NORTHEAST_3: ap-northeast-3
  AWS_SA_EAST_1: sa-east-1
  AWS_EU_WEST_3: eu-west-3
  AWS_AP_SOUTHEAST_3: ap-southeast-3
  AWS_EU_CENTRAL_2: eu-central-2
*/
// TODO: support more regions?
export const snowflakeToAwsRegionMap: Record<string, string> = {
  AWS_US_WEST_2: 'us-west-2',
  AWS_US_EAST_1: 'us-east-1',
  AWS_US_EAST_2: 'us-east-2',
  AWS_EU_WEST_1: 'eu-west-1',
  AWS_EU_CENTRAL_1: 'eu-central-1',
}

export interface LakehouseDistributionTable {
  name: string
}

export interface LakehouseDistributionAccessGrant {
  customerExternalRoleArn: string
  customerExternalIds: string[]
}

export interface LakehouseDistributionAccess {
  id: string
  inviteToken: string
  inviteTokenTtl: string
  distributionRoleArn: string
  accessGrants: LakehouseDistributionAccessGrant[]
}

export interface LakehouseDistribution {
  name: string
  tables: LakehouseDistributionTable[]
  s3Location: string
  distributionAccesses: LakehouseDistributionAccess[]
}

export interface LakehouseExternalAccess {
  accessIdentifier: string
  status: 'PENDING_CONNECTION' | 'CONNECTION_REQUESTED' | 'CONNECTED' | 'CONNECTION_FAILED'
  productKey: string
  tableName: string
  grantedEmailAddress: string
  grantedEmailToken: string
  grantedEmailTokenTtl: string
  externalRoleArn?: string
  externalIds?: string[]
}

export interface LakehouseShareAppConfig {
  tables: LakehouseTable[]
  distributions?: LakehouseDistribution[]
}

export const parseLakehouseShareAppConfig = (shareAppConfig?: string): LakehouseShareAppConfig => {
  const emptyConfig: LakehouseShareAppConfig = {
    tables: [],
    distributions: [],
  }
  if (!shareAppConfig) {
    return emptyConfig
  }
  try {
    return JSON.parse(shareAppConfig) as LakehouseShareAppConfig
  } catch (e) {
    console.error('Error parsing Lakehouse share app config', e)
    return emptyConfig
  }
}

export const lakehouseRegionOptions = AWS_REGIONS.map(({ value, label, displayAlt }) => ({
  value,
  label: displayAlt,
  description: label,
}))

type SslModeMap = Record<string, MySqlSslMode | PostgresSslMode>
type ConnectionPayload = LakehousePostgresConnection | LakehouseMySqlConnection

// Helper function to normalize SSL mode string
const normalizeSslMode = (mode: string): string => {
  return mode.toLowerCase().replace(/[-_]/g, '')
}

// Helper function to get SSL mode from connection string
export const getSslModeFromConnectionString = (
  params: URLSearchParams,
  sslModeMap: SslModeMap,
): MySqlSslMode | PostgresSslMode => {
  const sslMode = params.get('sslmode')?.toLowerCase() || 'prefer'
  const normalizedMode = normalizeSslMode(sslMode)
  const mappedMode = sslModeMap[normalizedMode]

  if (!mappedMode) {
    logger('Invalid SSL mode in connection string:', sslMode)
    throw new Error(`Invalid SSL mode: ${sslMode}. Valid modes are: ${Object.keys(sslModeMap).join(', ')}`)
  }

  return mappedMode
}

interface ExtractDbConnectionOptions {
  connectionStringValue?: string
  connectionDetails?: Partial<LakehousePostgresConnectionInput | LakehouseMySqlConnectionInput>
  sslModeMap: SslModeMap
  form: any
  fieldPrefix: string
  defaultPort: number
  requiredFields: (keyof LakehousePostgresConnection | LakehouseMySqlConnection)[]
  connectionType: LakehouseConnectionTypeEnum.Postgres | LakehouseConnectionTypeEnum.Mysql
}

export function extractDbConnectionPayload(opts: ExtractDbConnectionOptions) {
  const {
    connectionStringValue,
    connectionDetails,
    sslModeMap,
    form,
    fieldPrefix,
    defaultPort,
    requiredFields,
    connectionType,
  } = opts

  let connectionPayload: ConnectionPayload

  if (connectionStringValue) {
    try {
      const url = new URL(connectionStringValue)
      const params = url.searchParams

      const portStr = url.port || String(defaultPort)
      const parsedPort = parseInt(portStr, 10)
      if (isNaN(parsedPort) || parsedPort <= 0 || parsedPort > 65535) {
        throw new Error('Invalid port number in connection string.')
      }

      const dbName = url.pathname ? url.pathname.substring(1) : undefined
      if (!dbName) {
        throw new Error('Database name is missing in connection string.')
      }

      const sslMode =
        connectionType === 'POSTGRES'
          ? (getSslModeFromConnectionString(params, sslModeMap) as PostgresSslMode)
          : (getSslModeFromConnectionString(params, sslModeMap) as MySqlSslMode)

      connectionPayload = {
        host: url.hostname,
        port: parsedPort,
        database: dbName,
        username: url.username,
        password: url.password,
        ssl: sslMode,
        ...(connectionType === 'POSTGRES' && { schema: params.get('schema') || 'public' }),
      } as LakehousePostgresConnection | LakehouseMySqlConnection

      form.setFieldValue(`${fieldPrefix}.host`, url.hostname)
      form.setFieldValue(`${fieldPrefix}.port`, parsedPort)
      form.setFieldValue(`${fieldPrefix}.database`, dbName)
      form.setFieldValue(`${fieldPrefix}.username`, url.username)
      form.setFieldValue(`${fieldPrefix}.password`, url.password)
      form.setFieldValue(`${fieldPrefix}.ssl`, sslMode)
      connectionType === 'POSTGRES' && form.setFieldValue(`${fieldPrefix}.schema`, params.get('schema') || 'public')
    } catch (error: any) {
      logger('Error parsing connection string:', error)
      notify.error(`Invalid connection string: ${error.message || 'Check format.'}`)
      throw new Error('Invalid connection string')
    }
  } else {
    const details = connectionDetails as Partial<LakehousePostgresConnection | LakehouseMySqlConnection>
    let port = defaultPort
    if (details?.port) {
      const parsedPort = parseInt(String(details.port), 10)
      if (!isNaN(parsedPort) && parsedPort > 0 && parsedPort <= 65535) {
        port = parsedPort
      } else {
        logger('Invalid port number provided in individual fields:', details.port)
        notify.error('Invalid port number provided. Must be between 1 and 65535.')
        throw new Error('Invalid port number')
      }
    } else {
      notify.error('Port is required when not using a connection string.')
      throw new Error('Missing port number')
    }

    connectionPayload = {
      ...details,
      port,
      ssl: details?.ssl,
    } as ConnectionPayload
  }

  for (const field of requiredFields) {
    const key = field as keyof typeof connectionPayload
    if (connectionPayload[key] === undefined || connectionPayload[key] === null || connectionPayload[key] === '') {
      notify.error('Missing required connection details. Please check your input.')
      throw new Error('Missing required connection details')
    }
  }

  return connectionPayload
}

export async function getOltpTables(
  client: ControlPlaneClient,
  uniName: string,
  nodeName: string,
  connectionOptions: ExtractDbConnectionOptions,
) {
  const connectionPayload = extractDbConnectionPayload(connectionOptions)

  logger(`Testing ${connectionOptions.connectionType} connection with payload:`, connectionPayload)

  let connectionInput
  if (connectionOptions.connectionType === LakehouseConnectionTypeEnum.Postgres) {
    // Ensure the payload is typed as LakehousePostgresConnectionInput
    const postgresPayload = connectionPayload as LakehousePostgresConnectionInput
    connectionInput = {
      type: LakehouseConnectionTypeEnum.Postgres,
      postgresConnection: postgresPayload,
    }
  } else {
    // Ensure the payload is typed as LakehouseMySqlConnectionInput
    const mysqlPayload = connectionPayload as LakehouseMySqlConnectionInput
    connectionInput = {
      type: LakehouseConnectionTypeEnum.Mysql,
      mysqlConnection: mysqlPayload,
    }
  }

  const response = await client.graphqlFetch({
    query: GetLakehouseTablesDocument,
    variables: {
      uniName: uniName!,
      nodeName: nodeName!,
      connectionInput,
    },
  })

  return response
}
